import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { apiClient } from "../../../../api/client";
import { url } from "../../../../api";
import { useAppSelector } from "../../../../store/hooks";
import type { AOTDFormData, AOTDServerData, Tweet, GenerationStatusData } from "./types";

const API_PREFIX = `${url}/api/art-of-the-day/admin`;

function checkRole(str: string, searchStr: string): boolean {
  try {
    const arr = str.split(",");
    for (const item of arr) {
      if (item.trim() === searchStr) return true;
    }
    return false;
  } catch {
    return false;
  }
}

function ensureISOString(dateValue?: string | Date): string | undefined {
  if (!dateValue) return undefined;
  if (typeof dateValue === "string") {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateValue)) return dateValue;
    try {
      return new Date(dateValue).toISOString();
    } catch {
      return undefined;
    }
  }
  try {
    return dateValue.toISOString();
  } catch {
    return undefined;
  }
}

function toFormData(server: AOTDServerData): AOTDFormData {
  return {
    ...server,
    _id: server._id,
    __v: server.__v,
    date: ensureISOString(server.date),
    tweet: server.tweet
      ? {
          ...server.tweet,
          datePosted: ensureISOString(server.tweet.datePosted),
        }
      : undefined,
    generationStatus: server.generationStatus
      ? {
          ...server.generationStatus,
          startedAt: ensureISOString(server.generationStatus.startedAt),
          completedAt: ensureISOString(server.generationStatus.completedAt),
        }
      : undefined,
  };
}

// derive a YYYY-MM-DD dayKey from an AOTD doc, preferring dayState.dayKey
// (canonical) and falling back to the UTC slice of date.
export function aotdDayKey(data?: AOTDServerData | null): string | null {
  if (!data) return null;
  if (data.dayState?.dayKey) return data.dayState.dayKey;
  if (data.date) {
    try {
      return new Date(data.date).toISOString().slice(0, 10);
    } catch {
      return null;
    }
  }
  return null;
}

export function todayDayKey(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function shiftDayKey(dayKey: string, days: number): string {
  const d = new Date(`${dayKey}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface UseAotdAdmin {
  isAdmin: boolean;
  data: AOTDServerData | null;
  editable: Partial<AOTDFormData>;
  setEditable: React.Dispatch<React.SetStateAction<Partial<AOTDFormData>>>;
  selectedDate: string;
  isLoading: boolean;
  isMissing: boolean;
  errorMsg: string | null;
  fetchToday: () => Promise<void>;
  fetchByDate: (dayKey: string) => Promise<void>;
  fetchById: (id: string) => Promise<void>;
  shiftDay: (days: number) => Promise<void>;
  save: () => Promise<void>;
  destroy: () => Promise<void>;
}

export function useAotdAdmin(): UseAotdAdmin {
  const user = useAppSelector((s) => s.auth);
  const isAdmin = checkRole(user.role || "", "admin");

  const [data, setData] = useState<AOTDServerData | null>(null);
  const [editable, setEditable] = useState<Partial<AOTDFormData>>({});
  const [selectedDate, setSelectedDate] = useState<string>(todayDayKey());
  const [isLoading, setIsLoading] = useState(false);
  const [isMissing, setIsMissing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const applyServerData = useCallback((server: AOTDServerData) => {
    setData(server);
    setEditable(toFormData(server));
    setIsMissing(false);
  }, []);

  const clearData = useCallback(() => {
    setData(null);
    setEditable({});
  }, []);

  const fetchToday = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const res = await apiClient.get<AOTDServerData>(`${API_PREFIX}/today`, {});
      applyServerData(res.data);
      const dk = aotdDayKey(res.data) ?? todayDayKey();
      setSelectedDate(dk);
    } catch (err: any) {
      // 404 ("No AOTD found for today") is the normal pre-unlock state, not a
      // failure: this hook auto-fires on mount for every admin who opens the
      // AOTD page, so toasting it meant a red error toast every morning while
      // the page itself already renders the proper "shifted / available at"
      // box. Fall back to the same missing-day state `fetchByDate` uses. Real
      // failures (network, 5xx) still toast.
      if (err.response?.status === 404) {
        setIsMissing(true);
        setSelectedDate(todayDayKey());
        setErrorMsg(null);
        clearData();
      } else {
        console.error("[AOTD admin] fetch today failed:", err);
        const msg = err.response?.data?.message || "Failed to fetch today's AOTD.";
        setErrorMsg(msg);
        toast.error(msg);
        clearData();
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, applyServerData, clearData]);

  const fetchByDate = useCallback(
    async (dayKey: string) => {
      if (!isAdmin) return;
      setIsLoading(true);
      setErrorMsg(null);
      setSelectedDate(dayKey);
      try {
        const res = await apiClient.get<AOTDServerData>(
          `${API_PREFIX}/by-date/${dayKey}`,
          {},
        );
        applyServerData(res.data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setIsMissing(true);
          clearData();
          setErrorMsg(null);
        } else {
          console.error("[AOTD admin] fetch by date failed:", err);
          const msg = err.response?.data?.message || "Failed to fetch AOTD.";
          setErrorMsg(msg);
          toast.error(msg);
          clearData();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isAdmin, applyServerData, clearData],
  );

  const fetchById = useCallback(
    async (id: string) => {
      if (!isAdmin || !id.trim()) return;
      setIsLoading(true);
      setErrorMsg(null);
      try {
        const res = await apiClient.get<AOTDServerData>(
          `${API_PREFIX}/${id.trim()}`,
          {},
        );
        applyServerData(res.data);
        const dk = aotdDayKey(res.data);
        if (dk) setSelectedDate(dk);
      } catch (err: any) {
        console.error("[AOTD admin] fetch by id failed:", err);
        const msg = err.response?.data?.message || "Failed to fetch AOTD by ID.";
        setErrorMsg(msg);
        toast.error(msg);
        clearData();
      } finally {
        setIsLoading(false);
      }
    },
    [isAdmin, applyServerData, clearData],
  );

  const shiftDay = useCallback(
    async (days: number) => {
      const next = shiftDayKey(selectedDate, days);
      await fetchByDate(next);
    },
    [selectedDate, fetchByDate],
  );

  const save = useCallback(async () => {
    if (!data?._id || !isAdmin) {
      toast.error("No AOTD loaded or insufficient permissions.");
      return;
    }
    setIsLoading(true);
    setErrorMsg(null);

    const payload: Partial<AOTDServerData> = {};
    const eq = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

    if (
      editable.date !== undefined &&
      editable.date !== ensureISOString(data.date)
    ) {
      payload.date = editable.date;
    }
    const simple: Array<keyof AOTDFormData> = [
      "url",
      "createdBy",
      "authorQuote",
      "prompt",
      "title",
      "quoteHelp",
      "imageModel",
      "utcOffset",
    ];
    simple.forEach((k) => {
      if (editable[k] !== undefined && editable[k] !== (data as any)[k]) {
        (payload as any)[k] = editable[k];
      }
    });
    if (editable.views && !eq(editable.views, data.views)) {
      payload.views = editable.views;
    }
    if (editable.emojis && !eq(editable.emojis, data.emojis)) {
      payload.emojis = editable.emojis;
    }
    const origTweet = data.tweet
      ? { ...data.tweet, datePosted: ensureISOString(data.tweet.datePosted) }
      : undefined;
    if (editable.tweet && !eq(editable.tweet, origTweet)) {
      payload.tweet = editable.tweet as Tweet;
    }
    const origGen = data.generationStatus
      ? {
          ...data.generationStatus,
          startedAt: ensureISOString(data.generationStatus.startedAt),
          completedAt: ensureISOString(data.generationStatus.completedAt),
        }
      : undefined;
    if (
      editable.generationStatus &&
      !eq(editable.generationStatus, origGen)
    ) {
      payload.generationStatus =
        editable.generationStatus as GenerationStatusData;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes detected.");
      setIsLoading(false);
      return;
    }

    try {
      await apiClient.put(`${API_PREFIX}/${data._id}`, payload, {});
      toast.success("AOTD updated.");
      await fetchById(data._id);
    } catch (err: any) {
      console.error("[AOTD admin] save failed:", err);
      const msg = err.response?.data?.message || "Failed to update AOTD.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [data, editable, isAdmin, fetchById]);

  const destroy = useCallback(async () => {
    if (!data?._id || !isAdmin) {
      toast.error("No AOTD loaded or insufficient permissions.");
      return;
    }
    setIsLoading(true);
    setErrorMsg(null);
    try {
      await apiClient.delete(`${API_PREFIX}/${data._id}`, {});
      toast.success("AOTD deleted.");
      clearData();
    } catch (err: any) {
      console.error("[AOTD admin] delete failed:", err);
      const msg = err.response?.data?.message || "Failed to delete AOTD.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [data, isAdmin, clearData]);

  useEffect(() => {
    if (isAdmin) fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return {
    isAdmin,
    data,
    editable,
    setEditable,
    selectedDate,
    isLoading,
    isMissing,
    errorMsg,
    fetchToday,
    fetchByDate,
    fetchById,
    shiftDay,
    save,
    destroy,
  };
}
